/**
 * Configure Metrics — port of the original metricKeysModal() / metricKeyRow(). Sets the project's metric names
 * and what each number means. Names found only in the project's logs are listed read-only (greyed); such a
 * name joins the configured metrics only when a type is chosen for it.
 */
import { Fragment, useRef, useState } from 'react';
import type { MetricKind, Project, ProjectUpdate } from '@/types/models';
import { KINDS, isKind, kindOf } from '@/domain/metrics';
import { LIMITS } from '@/domain/validation';
import { useDataset } from '@/hooks/data';
import { ErrorState, LoadingState, ModalFrame } from '@/ui/kit';
import { useProjectPatch } from './useProjectPatch';

export function MetricKeysModal({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const ds = useDataset();
  const p = ds.data?.projects.find((x) => x.id === projectId);
  const title = `Configure Metrics${p ? ` — ${p.name}` : ''}`;
  if (ds.error) return <ModalFrame title={title} onClose={onClose}><ErrorState error={ds.error} onRetry={ds.refetch} /></ModalFrame>;
  if (!ds.data) return <ModalFrame title={title} onClose={onClose}><LoadingState /></ModalFrame>;
  if (!p) return <ModalFrame title={title} onClose={onClose}><div className="empty">No records found.</div></ModalFrame>;
  return <MetricKeysForm key={p.id} project={p} updates={ds.data.updates} title={title} onClose={onClose} />;
}

interface Row {
  key: number;
  label: string;
  kind: MetricKind | '';
  /** Name found in the project's logs (not configured): read-only */
  found: boolean;
}

function MetricKeysForm({ project: p, updates, title, onClose }: { project: Project; updates: ProjectUpdate[]; title: string; onClose: () => void }) {
  const seq = useRef(0);
  const [rows, setRows] = useState<Row[]>(() => {
    const found = [...new Set(updates.filter((u) => u.projectId === p.id).flatMap((u) => (u.metrics || []).map((m) => String(m.label || '').trim())))].filter((l) => l && !p.metricKeys.includes(l));
    return [
      ...p.metricKeys.map((label) => ({ key: ++seq.current, label, kind: kindOf(p, label), found: false })),
      ...found.map((label) => ({ key: ++seq.current, label, kind: kindOf(p, label), found: true })),
    ];
  });
  const save = useProjectPatch(p.id);
  const setRow = (key: number, patch: Partial<Row>) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const submit = async () => {
    const list = rows.map((r) => ({ ...r, label: r.label.trim() })).filter((r) => r.label);
    const metricKeys = list.filter((r) => !r.found || r.kind).map((r) => r.label);
    const metricKinds: Project['metricKinds'] = {};
    list.forEach((r) => {
      if (isKind(r.kind)) metricKinds[r.label] = r.kind;
    });
    const ok = await save.run({ metricKeys, metricKinds }, 'Metrics updated successfully.');
    if (ok) onClose();
  };
  return (
    <ModalFrame
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn primary" onClick={() => void submit()} disabled={save.pending}>
            Save
          </button>
        </>
      }
    >
      <label>Metrics and what each number means</label>
      <div>
        {rows.map((r) => (
          <div className="kv-row" style={{ gridTemplateColumns: '1fr 150px 34px' }} key={r.key} data-found={r.found ? '1' : undefined}>
            <input
              value={r.label}
              placeholder="Metric name"
              maxLength={LIMITS.metricLabel}
              aria-label="Metric name"
              readOnly={r.found}
              title={r.found ? 'Name as recorded in Project Logs' : undefined}
              style={r.found ? { color: 'var(--muted)' } : undefined}
              onChange={(e) => setRow(r.key, { label: e.target.value })}
            />
            <select value={r.kind} aria-label={`Type of ${r.label || 'metric'}`} onChange={(e) => setRow(r.key, { kind: isKind(e.target.value) ? e.target.value : '' })}>
              <option value="">Not set</option>
              {(Object.keys(KINDS) as MetricKind[]).map((k) => (
                <option key={k} value={k}>
                  {KINDS[k][0]}
                </option>
              ))}
            </select>
            <button type="button" className="icon-btn danger" data-tip="Remove" aria-label="Remove" onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}>
              ✕
            </button>
          </div>
        ))}
      </div>
      <button type="button" className="btn sm" onClick={() => setRows((rs) => [...rs, { key: ++seq.current, label: '', kind: '', found: false }])}>
        + Add metric
      </button>
      <div className="hint" style={{ marginTop: 10 }}>
        {(Object.keys(KINDS) as MetricKind[]).map((k) => (
          <Fragment key={k}>
            <b>{KINDS[k][0]}</b> — {KINDS[k][1]}
            <br />
          </Fragment>
        ))}
        <b>Not set</b> — no Today / To date tag, no total and no change until you choose a type. Grey names were found in this project's logs.
      </div>
    </ModalFrame>
  );
}
